import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, Phone, Mail, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const navItems = [{
    name: t('nav.products'),
    href: '/products'
  }, {
    name: t('nav.beforeAfter'),
    href: '/before-after'
  }, {
    name: t('nav.treatmentAreas'),
    href: '/treatment-areas'
  }, {
    name: t('nav.laosMarket'),
    href: '/laos-market'
  }, {
    name: t('nav.faceAnalysis'),
    href: '/face-analysis'
  }, {
    name: t('nav.aboutUs'),
    href: '/about'
  }, {
    name: t('nav.blog'),
    href: '/blog'
  }, {
    name: t('nav.contactUs'),
    href: '/contact'
  }];
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'lo' : 'en');
  };
  return <header className="fixed top-0 left-0 right-0 z-40 transition-all duration-300" data-id="element-601">
      {/* Top Contact Bar */}
      <div className="bg-gray-100 py-2 hidden md:block" data-id="element-602">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between text-sm text-gray-600" data-id="element-603">
          <div className="flex space-x-6" data-id="element-604">
            <div className="flex items-center" data-id="element-605">
              <Phone size={14} className="mr-2 text-brand-copper" data-id="element-606" />
              <span data-id="element-607">065-242-5494</span>
            </div>
            <a href="https://www.facebook.com/singdermthailand" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-brand-copper transition-colors" data-id="element-608">
              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24" data-id="element-609">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-610" />
              </svg>
              <span data-id="element-611">singdermthailand</span>
            </a>
            <a href="https://www.tiktok.com/@singderm_thailand" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-brand-copper transition-colors" data-id="element-612">
              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24" data-id="element-613">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" data-id="element-614" />
              </svg>
              <span data-id="element-615">singderm_thailand</span>
            </a>
          </div>

          {/* Language Switcher */}
          <button onClick={toggleLanguage} className="flex items-center hover:text-brand-copper transition-colors font-medium" data-id="element-616">
            <Globe size={14} className="mr-1.5" data-id="element-617" />
            {language === 'en' ? 'ພາສາລາວ' : 'English'}
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <div className={`transition-all duration-300 ${isScrolled ? 'bg-brand-copper shadow-md py-3' : 'bg-brand-copper/95 backdrop-blur-sm py-4'}`} data-id="element-618">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-619">
          <div className="flex justify-between items-center" data-id="element-620">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 cursor-pointer" data-id="element-621">
              <img src="https://cdn.magicpatterns.com/uploads/fx5ikhxZRrcCdL4VuBBQQb/singderm-logo-w_(1).png" alt="Singderm" className="h-10 w-auto object-contain" data-id="element-622" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8" data-id="element-623">
              {navItems.map(item => <Link key={item.href} to={item.href} className={`text-sm font-medium transition-colors ${location.pathname === item.href ? 'text-white border-b-2 border-white pb-1' : 'text-white/80 hover:text-white'}`} data-id="element-624">
                  {item.name}
                </Link>)}
              <button className="text-white hover:text-brand-lightCopper transition-colors" data-id="element-625">
                <Search size={20} data-id="element-626" />
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center space-x-4" data-id="element-627">
              <button onClick={toggleLanguage} className="text-white hover:text-brand-lightCopper transition-colors text-sm font-medium" data-id="element-628">
                {language === 'en' ? 'LA' : 'EN'}
              </button>
              <button className="text-white hover:text-brand-lightCopper transition-colors" data-id="element-629">
                <Search size={20} data-id="element-630" />
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white hover:text-brand-lightCopper focus:outline-none" data-id="element-631">
                {mobileMenuOpen ? <X size={24} data-id="element-632" /> : <Menu size={24} data-id="element-633" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-634">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-brand-copper border-t border-brand-darkCopper overflow-hidden shadow-lg" data-id="element-635">
            <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-636">
              {/* Mobile Contact Info */}
              <div className="py-3 border-b border-brand-darkCopper mb-2 text-white/80 text-sm space-y-2" data-id="element-637">
                <div className="flex items-center" data-id="element-638">
                  <Phone size={14} className="mr-2" data-id="element-639" />
                  <span data-id="element-640">065-242-5494</span>
                </div>
                <div className="flex items-center" data-id="element-641">
                  <Mail size={14} className="mr-2" data-id="element-642" />
                  <span data-id="element-643">polamkt01@gmail.com</span>
                </div>
              </div>

              {navItems.map(item => <Link key={item.href} to={item.href} onClick={() => setMobileMenuOpen(false)} className={`block px-3 py-3 text-base font-medium rounded-md transition-colors ${location.pathname === item.href ? 'text-white bg-brand-darkCopper' : 'text-white hover:bg-brand-darkCopper'}`} data-id="element-644">
                  {item.name}
                </Link>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}